import React, { PureComponent } from 'react'
import { withRouter } from 'react-router'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { first, identity, isEmpty } from 'lodash'
import { HeaderPanel } from 'carbon-components-react'
import Header from './components/Header'
import Skeleton from './components/Skeleton'
import Accordion from './elements/NestedAccordion'
import FilterBarContext from './context'
import {
  applyVisibilityRules,
  getInitialState,
  getVisibiltyRules,
  getPreservedState,
  serverSideT,
  getAsyncDependencies,
  isAsyncSection
} from './utils'

import './index.scss'
class FilterBar extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      t: props.t,
      isExpanded: !!props.expanded,
      pathChanged: '',
      newSections: props.sections,
      filters: getInitialState(props.sections),
      visibilityRules: getVisibiltyRules(props.sections),
      asyncDeps: getAsyncDependencies(props.sections),
      didOnChangeOnMount: !this.props.onChangeOnMount
    }
  }

  onUpdate(prevProps) {
    const { filters, didOnChangeOnMount, pathChanged, asyncDeps } = this.state
    const {
      sections,
      onChange,
      onChangeValue,
      onChangeOnMount,
      location: { pathname },
      keepStateOnSectionChange
    } = this.props

    // When the page changes, the filter state is resetted
    if (pathname !== prevProps?.location?.pathname) {
      return this.setState(() => ({ filters: {} }))
    }

    // If the onChange is necessary at first render, it notifies the page the status of the filter
    if (!didOnChangeOnMount) {
      return this.setState(
        () => ({
          didOnChangeOnMount: true
        }),
        () => {
          onChange(filters)
          onChangeValue(pathChanged, filters)
        }
      )
    }

    if (prevProps.sections !== sections) {
      const visibilityRules = getVisibiltyRules(sections)
      let newFilters = getInitialState(sections)

      if (
        keepStateOnSectionChange &&
        pathname === prevProps?.location?.pathname
      ) {
        newFilters = getPreservedState(filters, newFilters)
      }

      const visibleFilters = applyVisibilityRules(newFilters, visibilityRules)
      const section = first(pathChanged)

      const isAsync = isAsyncSection(section, asyncDeps)

      return this.setState(
        () => ({
          visibilityRules,
          // if sections changed due an async section, it is not a first render
          didOnChangeOnMount: isAsync || !onChangeOnMount,
          filters: visibleFilters,
          newSections: [...sections]
        }),
        () => {
          onChange(visibleFilters)
          onChangeValue(pathChanged, visibleFilters)
        }
      )
    }
  }

  componentDidMount() {
    const { filters, visibilityRules } = this.state

    this.setState(() => ({
      filters: applyVisibilityRules(filters, visibilityRules)
    }))
  }

  componentDidUpdate(prevProps) {
    this.onUpdate(prevProps)
  }

  toggleFilterBar = () => {
    this.setState((state) => ({ isExpanded: !state.isExpanded }))
  }

  updateFilters = (updater, pathToValue) => {
    this.setState(
      (state) => {
        const { clonedFilters, clonedNewSections } = updater(
          state.filters,
          state.newSections
        )

        return {
          pathChanged: pathToValue,
          newSections: clonedNewSections,
          filters: applyVisibilityRules(clonedFilters, state.visibilityRules)
        }
      },
      () => {
        const { filters, pathChanged, asyncDeps, newSections } = this.state
        const section = first(pathChanged)

        if (isAsyncSection(section, asyncDeps)) {
          this.props.onFetchAsyncSections(
            filters,
            section,
            asyncDeps[section],
            newSections
          )
        } else {
          // Notify user
          this.props.onChange(filters)
          this.props.onChangeValue(pathChanged, filters)
        }
      }
    )
  }

  onResetValues = () => {
    this.setState(
      () => ({
        pathChanged: '',
        filters: getInitialState(this.props.sections)
      }),
      () => {
        const { filters, pathChanged } = this.state
        this.props.onReset()
        this.props.onChange(filters)
        this.props.onChangeValue(pathChanged, filters)
      }
    )
  }

  onToggleActiveFilters = () => {
    const { filters, pathChanged } = this.state
    this.props.onChange(filters)
    this.props.onChangeValue(pathChanged, filters)
    this.props.onToggleFilters()
  }

  renderSections() {
    const { filters } = this.state
    const { sections, isLoading } = this.props

    return isLoading || isEmpty(filters) ? (
      <Skeleton rows={10} />
    ) : (
      <div className='filter-body' data-test='filter-body'>
        <Accordion items={sections} onChange={this.updateFilters} />
      </div>
    )
  }

  render() {
    const { isExpanded, newSections } = this.state
    const {
      t,
      onReload,
      filterName,
      isActiveFiltersOpened,
      servicesConfig: servicesConfigProp
    } = this.props

    const {
      filterList,
      saveFilter: saveProp,
      translationNamespace
    } = servicesConfigProp

    const expandedClass = isExpanded ? 'open' : 'closed'
    const serverSideTFun = !isEmpty(servicesConfigProp)
      ? serverSideT(t, translationNamespace)
      : identity

    const servicesConfig = isEmpty(servicesConfigProp)
      ? {}
      : {
          filterList: () => filterList(filterName),
          saveFilter: () => saveProp(newSections)
        }

    const contextState = { ...this.state, t, serverSideT: serverSideTFun }

    return (
      <FilterBarContext.Provider value={contextState}>
        <HeaderPanel
          className={cn('filter-bar', expandedClass)}
          expanded={isExpanded}
          aria-label='filter'
          data-test='filter-bar'
        >
          <div
            className={cn('header-bar', expandedClass)}
            data-test='header-bar'
          >
            <Header
              t={t}
              isExpanded={isExpanded}
              toggleFilterBar={this.toggleFilterBar}
              onReset={this.onResetValues}
              onReload={onReload}
              isActiveFiltersOpened={isActiveFiltersOpened}
              onToggleFilters={this.onToggleActiveFilters}
              data-test='toggle-filter-bar'
              servicesConfig={servicesConfig}
            />
          </div>
          {isExpanded && this.renderSections()}
        </HeaderPanel>
      </FilterBarContext.Provider>
    )
  }
}

FilterBar.propTypes = {
  t: PropTypes.func,
  expanded: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.object),
  onReset: PropTypes.func,
  onReload: PropTypes.func,
  onChange: PropTypes.func,
  onChangeValue: PropTypes.func,
  onToggleFilters: PropTypes.func,
  isActiveFiltersOpened: PropTypes.bool,
  onChangeOnMount: PropTypes.bool,
  keepStateOnSectionChange: PropTypes.bool,
  servicesConfig: PropTypes.shape({
    saveFilter: PropTypes.func,
    filterList: PropTypes.func,
    translationNamespace: PropTypes.string
  })
}

FilterBar.defaultProps = {
  t: identity,
  expanded: false,
  sections: [],
  onReset: identity,
  onReload: identity,
  onChange: identity,
  onChangeValue: identity,
  onToggleFilters: identity,
  isActiveFiltersOpened: false,
  onChangeOnMount: false,
  keepStateOnSectionChange: false,
  servicesConfig: {}
}

export default withRouter(React.memo(FilterBar))
